import React, { useRef, useEffect, useMemo } from 'react';
import * as Chart from 'chart.js';

import {
  ChartContainer,
  HeaderContainer,
  HeaderTitle,
  HeaderSubtitle,
  ChartWrapper,
  LoadingContainer,
  LoadingSpinner,
  ChartCanvas,
} from './chart.style';

const AdminAnalyticsPie = ({
  metricsData,
  options = {},
  loading = false,
  className = '',
  onChartClick,
  onLegendClick,
}) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  // Default data
  const defaultData = [
    { id: 'total-enquires', value: 890, label: 'Total Enquires' },
    { id: 'pre-lodge-report', value: 230, label: 'Pre lodge Report' },
    { id: 'final-report', value: 100, label: 'Final Report' },
    { id: 'cancel-da', value: 128, label: 'Cancel DA' },
    { id: 'reviewing', value: 450, label: 'Reviewing' },
  ];

  const data = metricsData || defaultData;

  // Default options with customization
  const defaultOptions = {
    showLegend: true,
    legendPosition: 'right',
    showTooltips: true,
    animationDuration: 1000,
    showPercentage: true,
    colors: [
      '#3B82F6', // Blue
      '#10B981', // Green
      '#F59E0B', // Yellow
      '#EF4444', // Red
      '#8B5CF6', // Purple
      '#F97316', // Orange
      '#06B6D4', // Cyan
      '#84CC16', // Lime
    ],
    height: 400,
    title: 'Applications Analytics Overview',
    subtitle: 'Distribution of enquiries and reports',
    showSummaryCards: true,
    responsive: true,
    maintainAspectRatio: false,
  };

  // Merge user options with defaults
  const chartOptions = useMemo(
    () => ({
      ...defaultOptions,
      ...options,
      colors: options.colors || defaultOptions.colors,
    }),
    [options]
  );

  // Chart.js configuration
  const chartConfig = useMemo(
    () => ({
      type: 'pie',
      data: {
        labels: data.map((item) => item.label),
        datasets: [
          {
            data: data.map((item) => item.value),
            backgroundColor: chartOptions.colors.slice(0, data.length),
            borderColor: chartOptions.colors.slice(0, data.length).map((color) => color + 'CC'),
            borderWidth: 2,
            hoverBorderWidth: 3,
            hoverBorderColor: '#ffffff',
            hoverBackgroundColor: chartOptions.colors
              .slice(0, data.length)
              .map((color) => color + 'DD'),
          },
        ],
      },
      options: {
        responsive: chartOptions.responsive,
        maintainAspectRatio: chartOptions.maintainAspectRatio,
        onClick: (event, elements) => {
          if (onChartClick && elements.length > 0) {
            const elementIndex = elements[0].index;
            const clickedData = data[elementIndex];
            onChartClick(clickedData, event);
          }
        },
        plugins: {
          legend: {
            display: chartOptions.showLegend,
            position: chartOptions.legendPosition,
            onClick: (event, legendItem, legend) => {
              // Default toggle behavior
              const index = legendItem.datasetIndex || legendItem.index;
              const chart = legend.chart;
              const meta = chart.getDatasetMeta(0);

              meta.data[index].hidden = !meta.data[index].hidden;
              chart.update();

              // Custom callback
              if (onLegendClick) {
                onLegendClick(legendItem, event);
              }
            },
            onHover: (event, legendItem, legend) => {
              // Set pointer cursor on legend hover
              legend.chart.canvas.style.cursor = 'pointer';
            },
            onLeave: (event, legendItem, legend) => {
              // Reset cursor when leaving legend
              legend.chart.canvas.style.cursor = 'default';
            },
            labels: {
              padding: 20,
              usePointStyle: true,
              pointStyle: 'circle',
              font: {
                size: 12,
                family: 'Inter, sans-serif',
              },
              color: '#374151',
              boxWidth: 12,
              boxHeight: 12,
              // Add strikethrough effect for hidden datasets
              generateLabels: function (chart) {
                const data = chart.data;
                if (data.labels.length && data.datasets.length) {
                  const meta = chart.getDatasetMeta(0);
                  return data.labels.map((label, i) => {
                    const isHidden = meta.data[i].hidden;
                    return {
                      text: label,
                      fillStyle: data.datasets[0].backgroundColor[i],
                      strokeStyle: data.datasets[0].borderColor[i],
                      lineWidth: data.datasets[0].borderWidth,
                      hidden: isHidden,
                      index: i,
                      // Add strikethrough styling for hidden items
                      fontStyle: isHidden ? 'italic' : 'normal',
                      fontColor: isHidden ? '#9CA3AF' : '#374151',
                      textDecoration: isHidden ? 'line-through' : 'none',
                    };
                  });
                }
                return [];
              },
            },
          },
          tooltip: {
            enabled: chartOptions.showTooltips,
            backgroundColor: '#1F2937',
            titleColor: '#F9FAFB',
            bodyColor: '#F9FAFB',
            borderColor: '#374151',
            borderWidth: 1,
            cornerRadius: 8,
            displayColors: true,
            titleFont: {
              size: 14,
              weight: 'bold',
            },
            bodyFont: {
              size: 12,
            },
            callbacks: {
              label: function (context) {
                const label = context.label || '';
                const value = context.parsed;

                if (chartOptions.showPercentage) {
                  const total = context.dataset.data.reduce((a, b) => a + b, 0);
                  const percentage = ((value / total) * 100).toFixed(1);
                  return `${label}: ${value.toLocaleString()} (${percentage}%)`;
                }

                return `${label}: ${value.toLocaleString()}`;
              },
            },
          },
        },
        interaction: {
          intersect: false,
          mode: 'index',
        },
        animation: {
          animateRotate: true,
          animateScale: true,
          duration: chartOptions.animationDuration,
        },
        hover: {
          mode: 'nearest',
          intersect: true,
        },
      },
    }),
    [data, chartOptions, onChartClick, onLegendClick]
  );

  useEffect(() => {
    Chart.Chart.register(
      Chart.PieController,
      Chart.DoughnutController,
      Chart.ArcElement,
      Chart.Tooltip,
      Chart.Legend,
      Chart.CategoryScale,
      Chart.LinearScale
    );

    if (chartRef.current && !loading) {
      // Destroy existing chart if it exists
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }

      const ctx = chartRef.current.getContext('2d');
      chartInstance.current = new Chart.Chart(ctx, chartConfig);
    }

    // Cleanup function
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [chartConfig, loading]);

  // Loading component
  const renderLoading = () => (
    <LoadingContainer>
      <LoadingSpinner />
    </LoadingContainer>
  );

  // Render header
  const renderHeader = () => {
    if (!chartOptions.title && !chartOptions.subtitle) return null;

    return (
      <HeaderContainer>
        {chartOptions.title && <HeaderTitle>{chartOptions.title}</HeaderTitle>}
        {chartOptions.subtitle && <HeaderSubtitle>{chartOptions.subtitle}</HeaderSubtitle>}
      </HeaderContainer>
    );
  };

  return (
    <ChartContainer className={className}>
      {renderHeader()}

      <ChartWrapper height={chartOptions.height}>
        {loading ? renderLoading() : <ChartCanvas ref={chartRef} />}
      </ChartWrapper>
    </ChartContainer>
  );
};

export default AdminAnalyticsPie;
